import { ArrowDown, ArrowUp, ExternalLink, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/ui/button";
import { cn } from "@/utils/tailwind";

import type { CreatorPost } from "./types";
import { getXPostId } from "./url";
import { XPostEmbed } from "./x-post-embed";

interface PostCardProps {
  post: CreatorPost;
  context?: string;
  creatorHandle?: string;
  sequence?: number;
  canMoveDown?: boolean;
  canMoveUp?: boolean;
  canEdit?: boolean;
  canLoadFallback?: boolean;
  canRemove?: boolean;
  onEdit?: (postId: string) => void;
  onMoveDown?: (postId: string) => void;
  onMoveUp?: (postId: string) => void;
  onRemove?: (postId: string) => void;
}

// Lead with the original X post and show optional creator context beneath it.
//
export function PostCard({
  post,
  context,
  creatorHandle,
  canMoveDown = false,
  canMoveUp = false,
  canEdit = true,
  canLoadFallback = true,
  canRemove = true,
  onEdit,
  onMoveDown,
  onMoveUp,
  onRemove,
}: PostCardProps) {
  const postId = getXPostId(post.url);
  const note = context ?? post.context;
  const hasActions = Boolean(onEdit || onMoveUp || onMoveDown || onRemove);

  // Let the official widget supply the post's frame and typography. Extra
  // card padding would squeeze its content, especially on narrow screens.
  //
  return (
    <article
      className="mx-auto w-full max-w-[550px] min-w-0 scroll-mt-24"
      id={`post-${post.id}`}
    >
      {postId ? (
        <XPostEmbed
          fallbackTitle="X post"
          postId={postId}
          {...(creatorHandle && canLoadFallback
            ? {
                fallbackUrl: `/embed/${encodeURIComponent(creatorHandle)}/${postId}`,
              }
            : {})}
        />
      ) : (
        <p className="text-muted-foreground text-sm">
          Open the original link to view this post.
        </p>
      )}
      {note.trim() && (
        <div className="bg-note/60 text-note-foreground border-signal/60 mx-1 mt-3 rounded-r-lg border-l-2 px-3 py-2.5 text-sm leading-6">
          <p className="text-muted-foreground mb-1 text-xs font-semibold">
            Creator context
          </p>
          <p className="break-words whitespace-pre-wrap">{note}</p>
        </div>
      )}
      <div
        className={cn(
          "mx-1 mt-2 flex flex-wrap items-center gap-1",
          hasActions && "border-t pt-2",
        )}
      >
        {onEdit && (
          <Button
            disabled={!canEdit}
            size="sm"
            type="button"
            variant="ghost"
            onClick={() => onEdit(post.id)}
          >
            <Pencil className="size-3.5" />
            {note.trim() ? "Edit context" : "Add context"}
          </Button>
        )}
        {onMoveUp && (
          <Button
            aria-label="Move post earlier"
            disabled={!canMoveUp}
            size="icon"
            type="button"
            variant="ghost"
            onClick={() => onMoveUp(post.id)}
          >
            <ArrowUp className="size-4" />
          </Button>
        )}
        {onMoveDown && (
          <Button
            aria-label="Move post later"
            disabled={!canMoveDown}
            size="icon"
            type="button"
            variant="ghost"
            onClick={() => onMoveDown(post.id)}
          >
            <ArrowDown className="size-4" />
          </Button>
        )}
        {onRemove && (
          <Button
            aria-label="Remove post from collection"
            disabled={!canRemove}
            size="icon"
            type="button"
            variant="ghost"
            onClick={() => onRemove(post.id)}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
        <Button
          asChild
          className="text-muted-foreground ml-auto h-8 px-2 text-xs"
          size="sm"
          variant="ghost"
        >
          <a href={post.url} rel="noreferrer" target="_blank">
            View on X<ExternalLink className="size-3.5" />
          </a>
        </Button>
      </div>
    </article>
  );
}
